import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi } from '../api/authApi';

// ── Storage strategy ─────────────────────────────────────────
// ACCESS TOKEN  → Redux memory only (never stored in localStorage/sessionStorage).
//                 Lost on page refresh, but restored automatically via the refresh
//                 endpoint which reads the httpOnly cookie.
// REFRESH TOKEN → httpOnly cookie set by the server. JS cannot read or steal it.
// USER INFO     → sessionStorage only (non-sensitive, for UX — name, role, modules).
//                 Cleared when the browser tab is closed.

const USER_KEY = 'uo_user';

const persistUser = (user) => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
};

const clearUser = () => {
  sessionStorage.removeItem(USER_KEY);
  // Belt-and-suspenders: also clear any legacy localStorage tokens from old versions
  localStorage.removeItem('uo_access_token');
  localStorage.removeItem('uo_refresh_token');
  localStorage.removeItem('uo_user');
};

const loadUserFromStorage = () => {
  try {
    return JSON.parse(sessionStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
};

// ── Thunks ──────────────────────────────────────────────────
export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ subdomain, username, password }, { rejectWithValue }) => {
    try {
      const data = await authApi.login({ subdomain, username, password });
      if (data.mfaRequired) {
        // MFA challenge — return without persisting user yet
        return data;
      }
      persistUser(data.user);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Login failed');
    }
  }
);

export const mfaVerifyThunk = createAsyncThunk(
  'auth/mfaVerify',
  async ({ mfaToken, code }, { rejectWithValue }) => {
    try {
      const data = await authApi.mfaVerify({ mfaToken, code });
      persistUser(data.user);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Invalid code');
    }
  }
);

export const refreshThunk = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      // No token needed — browser sends httpOnly cookie automatically (withCredentials: true)
      const data = await authApi.refresh();
      persistUser(data.user);
      return data;
    } catch (err) {
      clearUser();
      return rejectWithValue('Session expired');
    }
  }
);

// ── Initial state ────────────────────────────────────────────
// Access token starts null — restored by dispatching refreshThunk on app load.
// User info loaded from sessionStorage for immediate UX (name/role shown instantly).
const storedUser = loadUserFromStorage();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:        storedUser || null,
    accessToken: null,              // Memory only — never persisted
    loading:     false,
    error:       null,
    mfaRequired: false,             // true when login returns MFA challenge
    mfaToken:    null,              // short-lived challenge token
    mfaSetupRequired: false,        // true when tenant requires MFA but user hasn't enrolled yet
  },

  reducers: {
    logout(state) {
      clearUser();
      state.user        = null;
      state.accessToken = null;
      state.error       = null;
      state.mfaRequired = false;
      state.mfaToken    = null;
      state.mfaSetupRequired = false;
    },
    clearError(state) {
      state.error = null;
    },
    clearMfa(state) {
      state.mfaRequired = false;
      state.mfaToken    = null;
      state.mfaSetupRequired = false;
    },
    clearMfaSetup(state) {
      state.mfaSetupRequired = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginThunk.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.mfaRequired) {
          state.mfaRequired = true;
          state.mfaToken    = payload.mfaToken;
        } else if (payload.mfaSetupRequired) {
          // Tenant requires MFA, user must enroll — issue full JWT but force setup page
          state.accessToken      = payload.accessToken;
          state.user             = payload.user;
          state.mfaSetupRequired = true;
          state.mfaRequired      = false;
          state.mfaToken         = null;
        } else {
          state.accessToken = payload.accessToken;
          state.user        = payload.user;
          state.mfaRequired = false;
          state.mfaToken    = null;
          state.mfaSetupRequired = false;
        }
      })
      .addCase(loginThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error   = payload;
      })
      // MFA verify
      .addCase(mfaVerifyThunk.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(mfaVerifyThunk.fulfilled, (state, { payload }) => {
        state.loading     = false;
        state.accessToken = payload.accessToken;
        state.user        = payload.user;
        state.mfaRequired = false;
        state.mfaToken    = null;
      })
      .addCase(mfaVerifyThunk.rejected, (state, { payload }) => {
        state.loading = false;
        state.error   = payload;
      })
      // Refresh
      .addCase(refreshThunk.fulfilled, (state, { payload }) => {
        state.accessToken = payload.accessToken;
        state.user        = payload.user;
      })
      .addCase(refreshThunk.rejected, (state) => {
        clearUser();
        state.user        = null;
        state.accessToken = null;
      });
  },
});

export const { logout, clearError, clearMfa, clearMfaSetup } = authSlice.actions;

// ── Selectors ────────────────────────────────────────────────
export const selectUser                  = (s) => s.auth.user;
export const selectToken                 = (s) => s.auth.accessToken;
// Authenticated if we have a valid access token in memory
export const selectIsAuth                = (s) => !!s.auth.accessToken;
export const selectRole                  = (s) => s.auth.user?.role;
export const selectModules               = (s) => s.auth.user?.modules || [];
export const selectIndustry              = (s) => s.auth.user?.industryType;
export const selectTenantCurrency        = (s) => s.auth.user?.tenantCurrency || 'INR';
export const selectTenantDateFormat      = (s) => s.auth.user?.dateFormat || 'DD/MM/YYYY';
export const selectTenantName            = (s) => s.auth.user?.tenantName || '';
export const selectTenantAddress         = (s) => s.auth.user?.tenantAddress || '';
export const selectTenantGstNumber       = (s) => s.auth.user?.tenantGstNumber || '';
export const selectTenantPhones          = (s) => s.auth.user?.tenantPhones || '';
export const selectTenantLogoUrl         = (s) => s.auth.user?.tenantLogoUrl || '';
export const selectAuthLoading           = (s) => s.auth.loading;
export const selectAuthError             = (s) => s.auth.error;
export const selectMfaRequired           = (s) => s.auth.mfaRequired;
export const selectMfaToken             = (s) => s.auth.mfaToken;
export const selectMfaSetupRequired     = (s) => s.auth.mfaSetupRequired;
export const selectSubscriptionWarning   = (s) => s.auth.user?.subscriptionWarning || null;
export const selectSubscriptionActive    = (s) => s.auth.user?.subscriptionActive ?? true;
export const selectPlanType              = (s) => s.auth.user?.planType || 'TRIAL';
export const selectDaysUntilExpiry       = (s) => s.auth.user?.daysUntilExpiry ?? null;

// UPI payment settings (delivered at login, no extra API call needed)
export const selectUpiPaymentEnabled     = (s) => s.auth.user?.upiPaymentEnabled ?? false;
export const selectUpiId                 = (s) => s.auth.user?.upiId || '';
export const selectUpiName               = (s) => s.auth.user?.upiName || '';
export const selectAuditLogEnabled       = (s) => s.auth.user?.auditLogEnabled ?? true;
export const selectNotificationChannels  = (s) => s.auth.user?.notificationChannels || 'BOTH';
export const selectPrintFormat           = (s) => s.auth.user?.printFormat || 'A4';
export const selectQzTrayEnabled         = (s) => s.auth.user?.qzTrayEnabled ?? false;
export const selectAdminTenantCleanupEnabled = (s) => s.auth.user?.adminTenantCleanupEnabled ?? false;

// ── Permission selectors ─────────────────────────────────────
/** Returns the full Set of permission IDs granted to the current user */
export const selectPermissions           = (s) => s.auth.user?.permissions || [];

/**
 * Curried selector — call inside a component like:
 *   const canWrite = useSelector(selectHasPermission('factory:orders:write'));
 */
export const selectHasPermission = (permissionId) => (s) =>
  (s.auth.user?.permissions || []).includes(permissionId);

// ── Feature flag selectors ───────────────────────────────────
/**
 * Returns the raw enabledFeatures set from the login response.
 * Entries may be "FEATURE_NAME" (tenant-wide) or "FEATURE_NAME:MODULE_CONTEXT".
 */
export const selectEnabledFeatures = (s) => s.auth.user?.enabledFeatures || [];

/**
 * Returns true if featureName is enabled (tenant-wide or for any module).
 * Matches both "WORKFORCE" and "WORKFORCE:FACTORY" style entries.
 * Usage: useSelector(selectIsFeatureEnabled('WORKFORCE'))
 */
export const selectIsFeatureEnabled = (featureName) => (s) => {
  const features = s.auth.user?.enabledFeatures || [];
  return features.some(f => f === featureName || f.startsWith(featureName + ':'));
};

/**
 * Returns the display label for a feature, falling back to the featureName.
 * e.g. selectFeatureLabel('WORKFORCE') => "HR" (if tenant renamed it)
 * Usage: useSelector(selectFeatureLabel('WORKFORCE'))
 */
export const selectFeatureLabel = (featureName) => (s) =>
  (s.auth.user?.featureLabels || {})[featureName] || featureName;

export const selectNavigationLabels = (s) => s.auth.user?.navigationLabels || {};

export const selectNavigationLabel = (navKey, fallback) => (s) =>
  (s.auth.user?.navigationLabels || {})[navKey] || fallback;

export default authSlice.reducer;
